import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users,
  UserCheck,
  UserMinus,
  Clock,
  CheckCircle,
  Warning,
  ArrowsClockwise,
  ShieldCheck,
} from '@phosphor-icons/react';
import { usuariosAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';

export default function GestionUsuarios() {
  const { user } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionType, setActionType] = useState(null); // 'aprobar' o 'rechazar'
  const [processing, setProcessing] = useState(false);

  const loadUsuarios = async () => {
    setLoading(true);
    try {
      const response = await usuariosAPI.getAll();
      setUsuarios(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_superuser) {
      loadUsuarios();
    }
  }, [user?.is_superuser]);

  // Solo super usuarios pueden acceder
  if (!user?.is_superuser) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleAction = (usuario, action) => {
    setSelectedUser(usuario);
    setActionType(action);
    setShowConfirmModal(true);
  };

  const confirmAction = async () => {
    if (!selectedUser || !actionType) return;
    
    setProcessing(true);
    try {
      if (actionType === 'aprobar') {
        await usuariosAPI.aprobar(selectedUser.id);
        toast.success(`Usuario ${selectedUser.nombre} aprobado exitosamente`);
      } else {
        await usuariosAPI.rechazar(selectedUser.id);
        toast.success(`Usuario ${selectedUser.nombre} eliminado`);
      }
      setShowConfirmModal(false);
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar la acción');
    } finally {
      setProcessing(false);
    }
  };

  const usuariosPendientes = usuarios.filter(u => !u.aprobado);
  const usuariosAprobados = usuarios.filter(u => u.aprobado);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <ArrowsClockwise className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="gestion-usuarios-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="text-[#E73425]" />
              Gestión de Usuarios
            </h1>
            <p className="text-muted-foreground">
              Apruebe o rechace solicitudes de registro de nuevos usuarios
            </p>
          </div>
          <Button variant="outline" onClick={loadUsuarios}>
            <ArrowsClockwise size={18} className="mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Usuarios Pendientes */}
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <Clock size={20} />
              Usuarios Pendientes de Aprobación
              {usuariosPendientes.length > 0 && (
                <Badge className="bg-amber-500 text-white ml-2">
                  {usuariosPendientes.length}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {usuariosPendientes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle size={48} className="mx-auto mb-2 text-emerald-500" />
                <p>No hay usuarios pendientes de aprobación</p>
              </div>
            ) : (
              <div className="space-y-3">
                {usuariosPendientes.map(usuario => (
                  <div
                    key={usuario.id}
                    className="flex items-center justify-between p-4 bg-white rounded-lg border shadow-sm"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <Users size={20} className="text-amber-600" />
                      </div>
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {usuario.nombre}
                          {usuario.is_superuser && (
                            <Badge variant="outline" className="text-xs">
                              <ShieldCheck size={12} className="mr-1" />
                              Super Usuario
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">{usuario.email}</div>
                        <div className="text-xs text-muted-foreground">
                          Registrado: {new Date(usuario.created_at).toLocaleDateString('es-DO')}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleAction(usuario, 'aprobar')}
                      >
                        <UserCheck size={16} className="mr-1" />
                        Aprobar
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleAction(usuario, 'rechazar')}
                      >
                        <UserMinus size={16} className="mr-1" />
                        Rechazar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Usuarios Aprobados */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle size={20} className="text-emerald-600" />
              Usuarios Aprobados
              <Badge variant="outline" className="ml-2">
                {usuariosAprobados.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {usuariosAprobados.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No hay usuarios aprobados</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="p-3 text-left">Usuario</th>
                      <th className="p-3 text-left">Email</th>
                      <th className="p-3 text-left">Tipo</th>
                      <th className="p-3 text-left">Fecha Registro</th>
                      <th className="p-3 text-center">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usuariosAprobados.map(usuario => (
                      <tr key={usuario.id} className="border-b hover:bg-muted/30">
                        <td className="p-3 font-medium">{usuario.nombre}</td>
                        <td className="p-3 text-muted-foreground">{usuario.email}</td>
                        <td className="p-3">
                          {usuario.is_superuser ? (
                            <Badge className="bg-purple-100 text-purple-700">
                              <ShieldCheck size={12} className="mr-1" />
                              Super Usuario
                            </Badge>
                          ) : (
                            <Badge variant="outline">Usuario</Badge>
                          )}
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {new Date(usuario.created_at).toLocaleDateString('es-DO')}
                        </td>
                        <td className="p-3 text-center">
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle size={12} className="mr-1" />
                            Aprobado
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal de Confirmación */}
        <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {actionType === 'aprobar' ? (
                  <>
                    <UserCheck className="text-emerald-600" />
                    Confirmar Aprobación
                  </>
                ) : (
                  <>
                    <Warning className="text-red-600" />
                    Confirmar Rechazo
                  </>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {actionType === 'aprobar' ? (
                <p>
                  ¿Está seguro que desea aprobar al usuario{' '}
                  <strong>{selectedUser?.nombre}</strong>?
                  <br />
                  <span className="text-sm text-muted-foreground">
                    El usuario podrá iniciar sesión y acceder al sistema.
                  </span>
                </p>
              ) : (
                <div className="space-y-3">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-800 text-sm">
                      <Warning className="inline mr-2" />
                      Esta acción eliminará permanentemente la cuenta del usuario.
                    </p>
                  </div>
                  <p>
                    ¿Está seguro que desea rechazar y eliminar al usuario{' '}
                    <strong>{selectedUser?.nombre}</strong>?
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowConfirmModal(false)}
                disabled={processing}
              >
                Cancelar
              </Button>
              <Button
                className={actionType === 'aprobar' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                variant={actionType === 'rechazar' ? 'destructive' : 'default'}
                onClick={confirmAction}
                disabled={processing}
              >
                {processing ? (
                  <ArrowsClockwise className="animate-spin mr-2" size={16} />
                ) : null}
                {actionType === 'aprobar' ? 'Aprobar Usuario' : 'Eliminar Usuario'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
